import React, { useState } from 'react';
import { 
  Typography, 
  Box, 
  Chip,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Collapse,
  IconButton,
  Divider
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import { VisitFrequencyChart } from './Charts/VisitFrequencyChart';
import InsightCard from './shared/InsightCard';

interface UsagePatternCardProps {
  data: {
    monthlyVisitAverage: number;
    daysSinceLastVisit: number;
    topMenus: Array<{ name: string; count: number }>;
    visitTrend: 'increasing' | 'stable' | 'decreasing';
  } | null;
  loading?: boolean;
  treatmentRecords?: Array<{
    id: string;
    treatmentDate: string;
    totalAmount: number;
  }>;
}

const UsagePatternCard: React.FC<UsagePatternCardProps> = ({ data, loading = false, treatmentRecords = [] }) => {
  const [expanded, setExpanded] = useState(false);



  const getTrendIcon = () => {
    if (!data) return null;
    switch (data.visitTrend) {
      case 'increasing':
        return <TrendingUpIcon sx={{ color: '#4CAF50' }} />;
      case 'decreasing':
        return <TrendingDownIcon sx={{ color: '#f44336' }} />;
      default:
        return <TrendingFlatIcon sx={{ color: '#FF9800' }} />;
    }
  };

  const getTrendLabel = () => {
    if (!data) return '';
    switch (data.visitTrend) {
      case 'increasing':
        return '増加傾向';
      case 'decreasing':
        return '減少傾向';
      default:
        return '安定';
    }
  };

  if (loading) {
    return (
      <InsightCard
        title="利用傾向分析"
        cardVariant="success"
      >
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="80%" height={24} />
        <Skeleton variant="text" width="70%" height={24} />
        <Skeleton variant="rectangular" height={100} />
      </InsightCard>
    );
  }

  if (!data) {
    return (
      <InsightCard
        title="利用傾向分析"
        cardVariant="success"
      >
        <Typography color="text.secondary">
          データがありません
        </Typography>
      </InsightCard>
    );
  }

  return (
    <InsightCard
      title="利用傾向分析"
      cardVariant="success"
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getTrendIcon()}
          <Typography variant="body2" color="text.secondary">
            {getTrendLabel()}
          </Typography>
        </Box>
      }
    >

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <EventIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                月平均来店回数
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              <CountUp end={data.monthlyVisitAverage} duration={2} decimals={1} />
              <Box component="span" sx={{ fontSize: '0.8em', ml: 0.5 }}>回</Box>
            </Typography>
          </Box>
        </motion.div>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccessTimeIcon sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              前回来店から
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ color: data.daysSinceLastVisit > 60 ? 'error.main' : 'text.primary' }}>
            {data.daysSinceLastVisit}日経過
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocalOfferIcon sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              人気メニューTOP3
            </Typography>
          </Box>
          <List dense disablePadding>
            {data.topMenus.map((menu, index) => (
              <ListItem key={index} disableGutters>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        {index + 1}. {menu.name}
                      </Typography>
                      <Chip 
                        label={`${menu.count}回`} 
                        size="small" 
                        sx={{ 
                          bgcolor: '#e8f5e9',
                          color: '#2e7d32',
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* グラフ展開ボタン */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{ 
              bgcolor: '#e8f5e9',
              '&:hover': { bgcolor: '#c8e6c9' }
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ShowChartIcon />}
          </IconButton>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            {expanded ? 'グラフを閉じる' : 'グラフを表示'}
          </Typography>
        </Box>

        {/* グラフエリア */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              来店頻度推移
            </Typography>
            <VisitFrequencyChart treatmentRecords={treatmentRecords} />
          </Box>
        </Collapse>
    </InsightCard>
  );
};

export default UsagePatternCard;